/**
 *@desc gallery详情
 *@author XinD
 *@date 2022/11/8
 */
import React, { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useAsyncEffect, useClickAway, useKeyPress } from "ahooks";
import { galleryStore, ImgAndPromptItemType } from "@/stores/gallery";
import { getPromptDetail, postOpusDetail } from "@/pages/Gallery/api";
import { langStore } from "@noli/business";
import { galleryLangRecords } from "@/pages/Gallery/lang";
import { usePromptViewCall } from "@/pages/Gallery/hooks";
import LeftContent from "@/pages/Gallery/components/GalleryDetail/components/LeftContent";
import RightContent from "@/pages/Gallery/components/GalleryDetail/components/RightContent";
import TopContent from "@/pages/Gallery/components/GalleryDetail/components/TopContent";
import EditingWorks from "@/pages/Gallery/components/GalleryDetail/components/EditingWorks";
import { PostWorkValues } from "@/components/bussiness/PublishForm/type";
import { AlbumData } from "@/pages/Gallery/components/Album";
import styles from "../index.module.scss";
import config from "@/config";
import { copy as copyText } from "@/utils/tool";

interface store {
  imgAndPromptList: ImgAndPromptItemType[];
  setImgAndPromptList(list: ImgAndPromptItemType[]): void;
}

interface GalleryDetailProps {
  // 作品ID（opusId和currentID 2选1）
  opusId?: number;
  // 当前选中的图片
  currentID?: number | string;
  // 弹窗背景颜色
  backgroundColor?: string;
  // 预览图list
  previewList?: ImgAndPromptItemType[];
  // 操作的store, 得实现相同接口
  store?: store;
  // 关闭
  onClose?: () => void;
  // 点赞接口成功后....
  onLike?: (image_id: number) => void;
  // 取消点赞接口成功后....
  onCancelLike?: (image_id: number) => void;
  // 添加相册接口成功后.... 1:添加相册 2:修改相册 3:移除相册
  onAlbum?: (type: 1 | 2 | 3, albumData: AlbumData) => void;
  // 关注/取消关注接口成功后....
  onFollow?: (user_id: string, follow: boolean) => void;
  // 删除作品/重新发布作品后....
  onRefresh?: (type: ReFreshType) => void; // 0 删除，1 重发
  // 接口失败....
  onError?: (e: any) => void;
  // 作品获取详情后...
  onDetail?: (data: any | null) => void;
  style?: CSSProperties;
}

export type ReFreshType = 0 | 1; // 0 删除，1 重发

const GalleryDetail: FC<GalleryDetailProps> = (props) => {
  const lang = langStore.tgt;
  const {
    backgroundColor,
    opusId,
    currentID,
    previewList,
    onClose,
    onLike,
    onCancelLike,
    onAlbum,
    onFollow,
    onRefresh,
    onError,
    store = galleryStore,
    onDetail,
    style,
  } = props;
  const { imgAndPromptList, setImgAndPromptList } = store;
  const [current, setCurrent] = useState<ImgAndPromptItemType>();
  const [currentViewImageId, setCurrentViewImageId] = useState<number>();
  const GalleryDetailWrapperRef = useRef<HTMLDivElement | null>(null);
  const EditWrapperRef = useRef<HTMLDivElement | null>(null);
  const EditingWorksRef = useRef<HTMLDivElement | null>(null);
  const [pageState, setPageState] = useState<1 | 2>(1); //1：常规详情 2：编辑作品
  const resultPreviewList = useMemo(() => {
    return previewList ?? imgAndPromptList;
  }, [imgAndPromptList, previewList]);

  useAsyncEffect(async () => {
    const isGallery = ["/gallery", "/arts"].some((item) => location.pathname.includes(item));
    isGallery && metaJson && (document.title = `画宇宙 - ${current?.opus_title}`);
    if (
      current?.image_id &&
      currentViewImageId !== current?.image_id &&
      ![1, 2, 3].includes(current?.status as number)
    ) {
      try {
        await usePromptViewCall(current?.image_id as number);
        setCurrentViewImageId(current?.image_id);
      } catch (e) {
        console.error(e);
      }
    }
  }, [current]);

  const fetchAndSetData = useCallback(
    async (imageId: number) => {
      const {
        data: { list },
      } = await getPromptDetail({ image_id: Number(imageId) });
      setImgAndPromptList(list);
    },
    [setImgAndPromptList],
  );

  useAsyncEffect(async () => {
    if (!previewList && (currentID || opusId)) {
      const {
        data: { list },
      } = opusId
        ? await postOpusDetail({ opus_id: opusId })
        : await getPromptDetail({ image_id: Number(currentID) })
            .then((res: any) => {
              console.log("getPromptDetail", "onDetail");
              onDetail?.(res.data.list);
              return res;
            })
            .catch(() => {
              onDetail?.([]);
              return { data: { list: [] } };
            });
      setImgAndPromptList(list);
    }
  }, [currentID, opusId, setImgAndPromptList]);
  useEffect(() => {
    resultPreviewList?.length &&
      setCurrent(
        currentID
          ? resultPreviewList.filter(
              (prod: ImgAndPromptItemType) => prod.image_id == (current?.image_id ?? currentID),
            )[0]
          : resultPreviewList[0],
      );
  }, [resultPreviewList]);
  // 点击功能区域外关闭弹窗
  useClickAway(
    (e) => {
      const isClickPortal = e.composedPath().some((node) => {
        const t = node as HTMLElement;
        if (t.classList) {
          return t.classList.contains("chakra-portal");
        }
        return false;
      });
      if (isClickPortal) return;
      onClose && onClose();
    },
    [GalleryDetailWrapperRef, EditWrapperRef],
    "mousedown",
  );
  // 点击esc调用onClose
  useKeyPress(
    (e) => e.key === "Escape",
    () => {
      onClose && onClose();
    },
  );
  // 复制至剪贴板
  const copy = (value: string, tips?: string) => {
    copyText(value, tips ?? galleryLangRecords[lang]["Copied-to-the-clipboard"]);
  };

  const metaJson = useMemo(() => {
    return current ? JSON.parse(current?.meta_json as string) : undefined;
  }, [current]);
  const editingWorksDefaultValue = useMemo(() => {
    const result: PostWorkValues = {
      images: resultPreviewList.map((item: ImgAndPromptItemType) => {
        return {
          src: item.image_url,
          id: item.image_id + "",
          meta: JSON.parse(item.meta_json),
        };
      }),
      title: current?.opus_title ?? "",
      description: current?.opus_describes ?? "",
      publicPrompt: !!current?.is_open,
    };
    return result;
  }, [current]);

  const handleLike = useCallback(
    (image_id: number) => {
      onLike && onLike(image_id);
      if (!previewList) {
        fetchAndSetData(image_id);
      }
    },
    [onLike, fetchAndSetData, previewList],
  );
  const handleCancelLike = useCallback(
    (image_id: number) => {
      onCancelLike && onCancelLike(image_id);
      if (!previewList) {
        fetchAndSetData(image_id);
      }
    },
    [onCancelLike, fetchAndSetData, previewList],
  );
  const handleAlbum = useCallback(
    (type: 1 | 2 | 3, albumData: AlbumData) => {
      onAlbum && onAlbum(type, albumData);
      if (!previewList) {
        fetchAndSetData(albumData.image_id);
      }
    },
    [onAlbum, fetchAndSetData, previewList],
  );

  return (
    <div
      className={styles.GalleryDetailWrapper}
      style={{ backgroundColor: backgroundColor ?? "#080808", ...(style ?? {}) }}
      ref={GalleryDetailWrapperRef}
    >
      {onClose && (
        <img
          src={`${config.buildCdnBaseUrl}assets/creator/close.png`}
          alt=""
          className={styles.close}
          onClick={onClose}
        />
      )}
      {pageState === 1 ? (
        <>
          <TopContent
            EditWrapperRef={EditWrapperRef}
            current={current}
            opusId={opusId}
            onPageState={setPageState}
            copy={copy}
            onClose={onClose}
            onRefresh={onRefresh}
            onError={onError}
          />
          <div className={styles.content}>
            <LeftContent
              resultPreviewList={resultPreviewList}
              current={current}
              metaJson={metaJson}
              setCurrent={setCurrent}
              onLike={handleLike}
              onCancelLike={handleCancelLike}
              onAlbum={handleAlbum}
              onError={onError}
            />
            <RightContent
              opusId={opusId}
              metaJson={metaJson}
              current={current}
              copy={copy}
              onFollow={onFollow}
              onError={onError}
            />
          </div>
        </>
      ) : (
        <EditingWorks
          opusId={opusId}
          defaultValues={editingWorksDefaultValue}
          onClose={onClose}
          onRefresh={onRefresh}
          onPageState={setPageState}
          onError={onError}
        />
      )}
    </div>
  );
};
export default observer(GalleryDetail);
